import { Link } from "@tanstack/react-router";
import type React from "react";
import { useEffect } from "react";
import {
	Card,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import type {
	ContentImage,
	DocSummary,
	PostSummary,
} from "@/lib/content-types";
import { cn } from "@/lib/utils";

export type ArticleCardVariant = "featured" | "grid" | "compact";
export type ContentPlacement =
	| "blog_featured"
	| "blog_list"
	| "blog_suggested"
	| "home_latest"
	| "docs_list";
export type DocumentCardVariant = "grid" | "compact";

type LocaleParams = Record<string, string>;

type ArticleCardPost = Pick<
	PostSummary,
	"slug" | "publishedAtLabel" | "title" | "imageUrl" | "illustration"
>;

export function ArticleImage({
	image,
	alt,
	className,
	sizes,
	loading = "lazy",
	fetchPriority,
}: {
	image: ContentImage | null;
	alt: string;
	className: string;
	sizes: string;
	loading?: "eager" | "lazy";
	fetchPriority?: "high" | "low" | "auto";
}) {
	if (!image)
		return <div className={cn(className, "bg-muted")} aria-hidden="true" />;
	return (
		<picture>
			{image.sources.map((source) => (
				<source
					key={source.type}
					type={source.type}
					srcSet={source.srcSet}
					sizes={sizes}
				/>
			))}
			<img
				src={image.src}
				alt={alt}
				className={className}
				width={image.width}
				height={image.height}
				sizes={sizes}
				loading={loading}
				fetchPriority={fetchPriority}
				decoding="async"
			/>
		</picture>
	);
}

export function ArticleCardLink({
	article,
	localeParams,
	placement,
	variant,
	featuredLabel,
}: {
	article: ArticleCardPost;
	localeParams: LocaleParams;
	placement: Extract<
		ContentPlacement,
		"blog_featured" | "blog_list" | "blog_suggested" | "home_latest"
	>;
	variant: ArticleCardVariant;
	featuredLabel?: string;
}) {
	const { capture } = useAnalytics();

	return (
		<Link
			to="/{-$locale}/blog/$slug"
			params={{ ...localeParams, slug: article.slug }}
			className="group block"
			onClick={() =>
				capture(ANALYTICS_EVENTS.contentClick, {
					content_type: "blog",
					slug: article.slug,
					title: article.title,
					placement,
				})
			}
		>
			{variant === "featured" ? (
				<div className="flex flex-col gap-8 md:flex-row md:items-center">
					<div className="aspect-[16/10] flex-1 overflow-hidden rounded-xl border">
						<ArticleImage
							image={article.illustration}
							sizes="(min-width: 1024px) 560px, calc(100vw - 48px)"
							alt={article.title}
							className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
						/>
					</div>
					<div className="flex-1">
						{featuredLabel ? (
							<p className="text-sm font-medium text-muted-foreground">
								{featuredLabel}
							</p>
						) : null}
						<h2 className="mt-2 text-3xl font-semibold tracking-tight group-hover:underline md:text-4xl">
							{article.title}
						</h2>
						<p className="mt-4 text-sm text-muted-foreground">
							{article.publishedAtLabel}
						</p>
					</div>
				</div>
			) : (
				<Card className="h-full gap-0 overflow-hidden py-0 transition-shadow group-hover:shadow-md">
					<div className="aspect-[16/10] overflow-hidden border-b">
						<ArticleImage
							image={article.illustration}
							sizes="(min-width: 1024px) 360px, (min-width: 768px) 50vw, calc(100vw - 48px)"
							alt={article.title}
							className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
						/>
					</div>
					<CardHeader className={variant === "compact" ? "py-4" : "py-6"}>
						<CardDescription>{article.publishedAtLabel}</CardDescription>
						<CardTitle
							className={cn(
								"line-clamp-2 group-hover:underline",
								variant === "compact" ? "text-base" : "text-lg",
							)}
						>
							{article.title}
						</CardTitle>
					</CardHeader>
				</Card>
			)}
		</Link>
	);
}

export function DocumentCardLink({
	doc,
	localeParams,
	description,
	variant,
}: {
	doc: DocSummary;
	localeParams: LocaleParams;
	description?: string;
	variant: DocumentCardVariant;
}) {
	const { capture } = useAnalytics();
	const isCompact = variant === "compact";

	return (
		<Link
			to="/{-$locale}/docs/$slug"
			params={{ ...localeParams, slug: doc.slug }}
			className="group block"
			onClick={
				isCompact
					? undefined
					: () =>
							capture(ANALYTICS_EVENTS.contentClick, {
								content_type: "doc",
								slug: doc.slug,
								title: doc.title,
								placement: "docs_list",
							})
			}
		>
			<Card
				className={cn(
					"h-full transition-shadow group-hover:shadow-md",
					isCompact && "py-4",
				)}
			>
				<CardHeader className={cn(isCompact && "px-4")}>
					<CardTitle
						className={cn(
							"group-hover:underline",
							isCompact ? "text-sm" : "text-lg",
						)}
					>
						{doc.title}
					</CardTitle>
					{!isCompact && description ? (
						<CardDescription>{description}</CardDescription>
					) : null}
				</CardHeader>
			</Card>
		</Link>
	);
}

/** Night hero for listings and documents (blog, public documents). */
export function ContentHero({
	label,
	title,
	description,
	children,
	containerClassName,
}: {
	label: string;
	title: string;
	description?: string;
	children?: React.ReactNode;
	containerClassName?: string;
}) {
	return (
		<section className="dark relative isolate overflow-clip bg-background text-foreground">
			<div aria-hidden="true" className="blueprint absolute inset-0 -z-10" />
			<div
				className={cn(
					"mx-auto w-full max-w-7xl space-y-6 px-6 pt-16 pb-16 md:pt-24 md:pb-20 lg:px-10",
					containerClassName,
				)}
			>
				<p className="flex items-center gap-3 font-mono text-[11px] font-medium tracking-[0.08em] text-muted-foreground uppercase">
					<span aria-hidden="true" className="text-signal-ink">
						{"//"}
					</span>
					{label}
				</p>
				<h1 className="max-w-4xl text-[clamp(2.1rem,4.4vw,3.9rem)] leading-[1.04] font-semibold tracking-[-0.055em] text-balance">
					{title}
				</h1>
				{description ? (
					<p className="max-w-2xl text-lg text-pretty text-muted-foreground">
						{description}
					</p>
				) : null}
				{children}
			</div>
		</section>
	);
}

export function ContentEmptyState({ children }: { children: React.ReactNode }) {
	return (
		<Card className="p-8 text-center text-muted-foreground">{children}</Card>
	);
}

export function ContentErrorState({
	error,
	title,
	fallback,
	source,
}: {
	error: Error;
	title: string;
	fallback: string;
	source: string;
}) {
	const { captureException } = useAnalytics();

	useEffect(() => {
		captureException(error, {
			source,
		});
	}, [captureException, error, source]);

	return (
		<div className="mx-auto max-w-5xl px-6 py-24 text-center">
			<h1 className="mb-3 text-2xl font-semibold text-destructive md:text-3xl">
				{title}
			</h1>
			<p className="text-muted-foreground">{error.message || fallback}</p>
		</div>
	);
}
